import * as React from 'react';
import { observer, Observer } from 'mobx-react-lite';
import { Table, Tooltip, Button, Popconfirm, Space } from 'antd';
import { SortOrder } from 'antd/lib/table/interface';
import { ColumnProps } from 'antd/lib/table';
import { TrainingProjectsStore } from '../../stores';
import { Utils } from '../../../common/services/Utils';
import { TrainingProjectModel } from '../../models';
import { TrainingSessionStatusTag } from '..';
import useTableScrollHeight from '../../../common/hooks/useTableScrollHeight';
import './TrainingProjectsList.less';

type Props = {
    store: TrainingProjectsStore;
};

const TrainingProjectsList: React.FC<Props> = ({ store }) => {
    const { scrollHeight } = useTableScrollHeight(200);

    const columns: ColumnProps<TrainingProjectModel>[] = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: (a: TrainingProjectModel, b: TrainingProjectModel) =>
                a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
            render: (_, trainingProject: TrainingProjectModel) => (
                <Observer>{() => <span>{trainingProject.name}</span>}</Observer>
            )
        },
        {
            title: 'Training Status',
            dataIndex: 'trainingStatus',
            key: 'trainingStatus',
            render: (_: unknown, trainingProject: TrainingProjectModel) => (
                <Observer>
                    {() => (
                        <TrainingSessionStatusTag
                            status={trainingProject.status?.latestSessionStatus}
                            tooltipText={trainingProject.statusTooltip}
                            noStatusLabel="No Sessions"
                        />
                    )}
                </Observer>
            )
        },
        {
            title: 'Reference Model',
            dataIndex: 'referenceModelId',
            key: 'referenceModelId',
            render: (_, trainingProject: TrainingProjectModel) => (
                <Observer>{() => <span>{store.getMlModelNameById(trainingProject.referenceModelId)}</span>}</Observer>
            )
        },
        {
            title: 'Created By',
            dataIndex: 'createdBy',
            key: 'createdBy',
            render: (createdBy: string) => store.getUserNameById(createdBy)
        },
        {
            title: 'Last Updated',
            dataIndex: 'updateDate',
            key: 'updateDate',
            defaultSortOrder: 'descend' as SortOrder,
            sorter: (a: TrainingProjectModel, b: TrainingProjectModel) =>
                Utils.safeDateCompare(a.updateDate || a.createDate, b.updateDate || b.createDate),
            render: (_, trainingProject: TrainingProjectModel) => (
                <Observer>
                    {() => (
                        <span>
                            {Utils.formatDateShort(
                                trainingProject.updateDate ? trainingProject.updateDate : trainingProject.createDate
                            )}
                        </span>
                    )}
                </Observer>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: unknown, trainingProject: TrainingProjectModel) => (
                <div className="row-actions-wrapper" onClick={e => e.stopPropagation()}>
                    <Space size="middle">
                        <Tooltip title="Edit training project">
                            <Button
                                size="small"
                                type="text"
                                style={{ verticalAlign: 'middle' }}
                                icon={<i className="alpha-icon lg edit-icon" style={{ marginTop: -3 }} />}
                                onClick={() => {
                                    trainingProject.setEditable(true);
                                    store.setCreateEditDialogVisible(true);
                                }}
                            />
                        </Tooltip>
                        <Tooltip title="Delete training project">
                            <Popconfirm
                                title={`Are you sure you want to delete training project "${trainingProject.name}"?`}
                                onConfirm={() => trainingProject.delete()}
                                okText="Delete"
                                cancelText="Cancel"
                                okButtonProps={{ danger: true }}
                            >
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<i className="alpha-icon lg delete-icon-red" />}
                                    danger
                                />
                            </Popconfirm>
                        </Tooltip>
                    </Space>
                </div>
            )
        }
    ];

    React.useEffect(() => {
        if (!store.project) {
            return;
        }

        store.loadTrainingProjectsStatuses(true);
        const loadStatusesInterval = setInterval(() => store.loadTrainingProjectsStatuses(false), 10000);
        return () => clearInterval(loadStatusesInterval);
    }, [store, store.project]);

    return (
        <Table
            data-id="training-projects-table"
            className="alpha-portal-table training-projects-list"
            loading={store.isLoading}
            dataSource={store.isLoading ? [] : store.trainingProjects.slice()}
            columns={columns}
            pagination={{ pageSize: 20, hideOnSinglePage: true, showSizeChanger: false }}
            scroll={{ y: scrollHeight }}
            rowKey={(trainingProject: TrainingProjectModel) => trainingProject.id}
            onRow={(trainingProject: TrainingProjectModel) => {
                return {
                    onClick: () => store.navigateToTrainingProjectDashboard(trainingProject.id)
                };
            }}
        />
    );
};

export default observer(TrainingProjectsList);
